import React from "react";
import {
  LegacyCard,
  InlineGrid,
  TextField,
  Select,
  Checkbox,
} from "@shopify/polaris";
import CustomAutocomplete from "../CustomAutocomplete";
import {
  ButtonNumber,
  SatinNumbers,
  threadContrastColor,
  NameLabelColor,
  } from "../CoatData";
import { useLibrary } from "../LibraryContext";

const WomenOvercoatForm = ({
  form,
  setForm,
  errors,
  baseUrl,
  setApploader,
  conditionalFieldsMap,
  viewOrder
}) => {
  const { fabrics: Fabrics, linings: LiningNumbers } = useLibrary();
  const ValueAllSelect = [...LiningNumbers, ...SatinNumbers, ...Fabrics];
  const handleChange =
    (section, key = null) =>
      (values) => {
        const trimmedKey = key?.trim();
        let value;
        if (Array.isArray(values)) {
          value = values[0];
        } else {
          value = values;
        }

        const sectionMap = conditionalFieldsMap[section];
        const fieldRule = sectionMap?.[trimmedKey];

        if (fieldRule) {
          const { conditionValue, dependentKey } = fieldRule;
          if (value === conditionValue) {
            // Add the dependent field
            setForm((prev) => ({
              ...prev,
              [section]: {
                ...prev[section],
                [trimmedKey]: value,
                [dependentKey]: "",
              },
            }));
          } else {
            // Remove the dependent field
            const updatedSection = { ...form[section] };
            delete updatedSection[dependentKey];
            updatedSection[trimmedKey] = value;

            setForm((prev) => ({
              ...prev,
              [section]: updatedSection,
            }));
          }

          return;
        }
        // Default update
        if (trimmedKey) {
          setForm((prev) => ({
            ...prev,
            [section]: {
              ...prev[section],
              [trimmedKey]: value,
            },
          }));
        } else {
          setForm((prev) => ({
            ...prev,
            [section]: value,
          }));
        }
      };

  const WomencoatFields = [
    {
      label: "Front Button",
      key: "frontButton",
      type: "select",
      options: [
        "3 Button",
        "1 Button",
        "2 Button",
        "3 Button with Roll",
        "4 Button",
        "5 Button",
        "DB 4 with 1 Buttoned",
        "DB 4 with 2 Buttoned",
        "DB 6 with 1 Buttoned",
        "DB 6 with 2 Buttoned",
      ],
    },
    {
      label: "Front Bottom",
      key: "frontBottom",
      type: "select",
      options: ["Straight", "Rounded"],
    },
    {
      label: "Lapel Style",
      key: "lapelStyle",
      type: "select",
      options: ["Notch", "Peak", "Shawl", "Napoleon", "PK", "Stand Collar", "Closed Collar"],
    },
    {
      label: "Lapel Buttonhole",
      key: "LapelButtonhole",
      type: "select",
      options: ["Yes", "No"],
    },
    {
      label: "Back Vent",
      key: "backVent",
      type: "select",
      options: ["Central", "Center with Back Belt", "Center with Full Belt and Loops", "No Vent", "Side Vent"],
    },
    {
      label: "Chest Pocket",
      key: "chestPocket",
      type: "select",
      options: ["No", "Regular", "Patch"],
    },
    {
      label: "Lower Pocket",
      key: "lowerPocket",
      type: "select",
      options: [
        "Welt Slanted",
        "Besom with Flap",
        "No Pocket",
        "Patch",
      ],
    },
    {
      label: "Ticket Pocket",
      key: "ticketPocket",
      type: "select",
      options: ["No", "Yes"],
    },
    {
      label: "Sleeve Button",
      key: "sleeveButton",
      type: "select",
      options: [
        "4 Kissing",
        "1 Button",
        "2 Kissing",
        "3 Kissing",
        "5 Kissing",
        "2 Button",
        "3 Button",
        "4 Button",
        "5 Button",
      ],
    },
    {
      label: "Contrast",
      key: "contrast",
      type: "select",
      options: [
        "Lapel",
      ],
    },
    {
      label: "Lining Style",
      key: "liningStyle",
      type: "select",
      options: ["Full", "1/2 lining", "1/4 lining", "No Lining"],
    },
    {
      label: "Lining Number",
      key: "liningNumber",
      type: "select",
      options: LiningNumbers,
    },
    {
      label: "Button Number",
      key: "buttonNumber",
      type: "select",
      options: ButtonNumber,
    },
    {
      label: "Thread Contrast Lapel Buttonhole",
      key: "threadContrastLapelButtonhole",
      type: "select",
      options: ["No", "Yes"],
    },
    {
      label: "Thread Contrast Sleeve",
      key: "threadContrastSleeve",
      type: "select",
      options: ["No", "Last Sleeve Butthonholes", "All Sleeve Buttonholes"],
    },
    {
      label: "Name Label Font",
      key: "nameLabelFont",
      type: "select",
      options: [
        "Caligraphy",
        "Times Roman",
        "Block",
        "Script",
        "Bradley",
        "Old English",
        "Custom Upload",
      ],
    },
    {
      label: "Name Label Color",
      key: "nameLabelColor",
      type: "select",
      options: NameLabelColor,
    },
    { label: "Name Label Text", key: "nameLabelText" },
  ];

  const monogramOptions = ["Label Right Side", "Collar Felt"];

  // 3. Add a change handler for monogram checkboxes
  const handleMonogramChange = (label, checked) => {
    setForm((prev) => {
      const updated = checked
        ? [...prev.womenOvercoat.monograms, label]
        : prev.womenOvercoat.monograms.filter((m) => m !== label);

      const updatedDetails = { ...prev.womenOvercoat.monogramDetails };
      if (checked && !updatedDetails[label]) {
        updatedDetails[label] = {
          font: "Calligraphy",
          color: "102 White",
        };
      }
      if (!checked) delete updatedDetails[label];

      return {
        ...prev,
        womenOvercoat: {
          ...prev.womenOvercoat,
          monograms: updated,
          monogramDetails: updatedDetails,
        },
      };
    });
  };

  const handleMonogramFieldChange = (label, key, value) => {
    setForm((prev) => ({
      ...prev,
      womenOvercoat: {
        ...prev.womenOvercoat,
        monogramDetails: {
          ...prev.womenOvercoat.monogramDetails,
          [label]: {
            ...prev.womenOvercoat.monogramDetails[label],
            [key]: value,
          },
        },
      },
    }));
  };

  const handleFileUpload = async (section, key, file) => {
    const formData = new FormData();
    formData.append("image", file);
    setApploader(true);
    try {
      const res = await fetch(`${baseUrl}/api/files/upload`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        const uploadedFilename = data.files;
        handleChange(section, key)(uploadedFilename);
        if (key === "fileimage") {
          handleMonogramFieldChange(section, key, uploadedFilename);
        }
        setApploader(false);
      } else {
        alert("File upload failed");
      }
    } catch (error) { }
  };

  const handleDownload = async (url) => {
    try {
      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) throw new Error("Network response was not ok");
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = url.split("/").pop() || "download.png";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
    }

  };

  const renderSection = (title, fields, sectionKey) => (
    <LegacyCard title={title} sectioned>
      <InlineGrid columns={{ xs: 1, sm: 1, md: 2, lg: 3 }} gap="300">
        {fields.map(({ label, key, type = "text", options = [] }) => {
          if (type === "select") {
            return (
              <React.Fragment key={key}>
                {key !== "liningNumber" &&
                  key !== "buttonNumber" &&
                  key !== "nameLabelColor" && (
                    <div className="two_culumn">
                      <Select
                        label={label}
                        options={options}
                        value={form[sectionKey]?.[key] || ""}
                        onChange={handleChange(sectionKey, key)}
                        error={errors[sectionKey]?.[key]}
                      />
                      {key === "chestPocketSqaure" &&
                        form[sectionKey]?.[key] === "Other" && (
                          <CustomAutocomplete
                            label="Lining Number "
                            optionsList={LiningNumbers}
                            onChange={handleChange(sectionKey, "chestPocketSqaureOther")}
                            defaultValue={
                              form[sectionKey]?.["chestPocketSqaureOther"] || ""
                            }
                          />
                        )}
                      {key === "contrast" && (
                        <CustomAutocomplete
                          label="Fabric / Satin / Lining"
                          optionsList={ValueAllSelect}
                          onChange={handleChange(sectionKey, "contrastSatin")}
                          defaultValue={form[sectionKey]?.["contrastSatin"] || ""}
                        />
                      )}
                      {key === "threadContrastLapelButtonhole" &&
                        form[sectionKey]?.[key] === "Yes" && (
                          <CustomAutocomplete
                            optionsList={threadContrastColor}
                            onChange={handleChange(sectionKey, "threadContrastLapelButtonholeYes")}
                            defaultValue={
                              form[sectionKey]?.[
                              "threadContrastLapelButtonholeYes"
                              ] || ""
                            }
                          />
                        )}
                      {key === "threadContrastSleeve" &&
                        (form[sectionKey]?.[key] === "Last Sleeve Butthonholes" ||
                          form[sectionKey]?.[key] ===
                          "All Sleeve Buttonholes") && (
                          <CustomAutocomplete
                            optionsList={threadContrastColor}
                            onChange={handleChange(sectionKey, "threadContrastSleeveColor")}
                            defaultValue={
                              form[sectionKey]?.["threadContrastSleeveColor"] ||
                              ""
                            }
                          />
                        )}
                      {key === "nameLabelFont" &&
                        form[sectionKey]?.[key] === "Custom Upload" && (
                          <input
                            type="file"
                            className="imgUpload"
                            key="nameLabelFontFile"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              handleFileUpload(
                                sectionKey,
                                "nameLabelFontFile",
                                file
                              );
                            }}
                            error={errors[sectionKey]?.["nameLabelFontFile"]}
                          />
                        )}
                      {viewOrder === 'yes' &&
                        form[sectionKey]?.[key] === "Custom Upload" && (
                          form.womenOvercoat.nameLabelFontFile ? (
                            <img
                              className="image_server"
                              onClick={() => handleDownload(form.womenOvercoat.nameLabelFontFile)}
                              src={form.womenOvercoat.nameLabelFontFile}
                              alt="Mark Russell"
                            />
                          ) : null
                        )}
                    </div>
                  )}
                {key === "liningNumber" && (
                  <CustomAutocomplete
                    label={label}
                    optionsList={options}
                    onChange={handleChange(sectionKey, key)}
                    defaultValue={form[sectionKey]?.[key] || ""}
                  />
                )}
                {key === "buttonNumber" && (
                  <CustomAutocomplete
                    label={label}
                    optionsList={options}
                    onChange={handleChange(sectionKey, key)}
                    defaultValue={form[sectionKey]?.[key] || ""}
                  />
                )}
                {key === "nameLabelColor" && (
                  <CustomAutocomplete
                    label={label}
                    optionsList={options}
                    onChange={handleChange(sectionKey, key)}
                    defaultValue={form[sectionKey]?.[key] || ""}
                  />
                )}
              </React.Fragment>
            );
          }
          return (
            <TextField
              key={key}
              label={label}
              value={form[sectionKey]?.[key] || ""}
              onChange={handleChange(sectionKey, key)}
              error={errors[sectionKey]?.[key]}
            />
          );
        })}
      </InlineGrid>
      <div className="multiple_option_checkbox">
        <h3>Add Monogram</h3>
        <InlineGrid columns={{ xs: 2, sm: 2, md: 3, lg: 6 }} gap="100">
          {monogramOptions.map((label) => (
            <Checkbox
              key={label}
              label={label}
              checked={form.womenOvercoat.monograms.includes(label)}
              onChange={(val) => handleMonogramChange(label, val)}
            />
          ))}
        </InlineGrid>
      </div>
      {form.womenOvercoat.monograms.map((label, index) => (
        <div
          key={`monogram-${label}-${index}`}
          className="monogram_option_list"
        >
          <h3>{`Monogram Details - ${label}`}</h3>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 2, lg: 3 }} gap="400">
            <div>
              <Select
                label="Font"
                options={[
                  "Caligraphy",
                  "Times Roman",
                  "Block",
                  "Script",
                  "Bradley",
                  "Old English",
                  "Custom Upload",
                ]}
                value={form.womenOvercoat.monogramDetails[label]?.font || ""}
                onChange={(val) =>
                  handleMonogramFieldChange(label, "font", val)
                }
              />
              {form.womenOvercoat.monogramDetails[label]?.font ===
                "Custom Upload" ? (
                <input
                  label="Upload Image"
                  className="fileUpload"
                  type="file"
                  name="image"
                  accept=".jpg, .jpeg, .png"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    handleFileUpload(label, "fileimage", file);
                  }}
                />
              ) : null}
              {viewOrder === 'yes' && (
                form.womenOvercoat.monogramDetails[label]?.fileimage ? (
                  <img
                    className="image_server"
                    onClick={() => handleDownload(form?.womenOvercoat?.monogramDetails?.[label]?.fileimage || "")}
                    src={form.womenOvercoat.monogramDetails[label].fileimage}
                    alt="Mark Russell"
                  />
                ) : null
              )}
            </div>
            <Select
              label="Color"
              options={NameLabelColor}
              value={form.womenOvercoat.monogramDetails[label]?.color || ""}
              onChange={(val) => handleMonogramFieldChange(label, "color", val)}
            />
            {form.womenOvercoat.monogramDetails[label]?.font !== "Custom Upload" && (
              <TextField
                label={
                  label === "Collar Felt"
                    ? "Collar Felt Text"
                    : "Label Right Side Text"
                }
                value={form.womenOvercoat.monogramDetails[label]?.font !== "Custom Upload" ? form.womenOvercoat.monogramDetails[label]?.text || "" : ""}
                onChange={(val) => handleMonogramFieldChange(label, "text", val)}
              />
            )}
          </InlineGrid>
        </div>
      ))}
    </LegacyCard>
  );

  return renderSection("Women Overcoat", WomencoatFields, "womenOvercoat");
};

export default WomenOvercoatForm;
